import { api, idempotencyHeaders } from './client';

// ---------- Identity probe -------------------------------------------------
export const fetchAdminMe = () => api.get('/api/admin/me').then((r) => r.data);

// ---------- Header ticker --------------------------------------------------
export const fetchAdminTicker = () => api.get('/api/admin/ticker').then((r) => r.data);

// ---------- Subpage 1: Users directory + override modal --------------------
// `dates`: { date_from?, date_to? } (signup date); every admin list below takes the same optional filter.
export const listAdminUsers = (page = 1, q = '', dates = {}) =>
  api.get('/api/admin/users', { params: { page, q: q || undefined, ...dates } }).then((r) => r.data);

// Patch v17.7 — God-admin-only impersonation. Backend mints a 60-min
// access token for the target user's identity. Frontend opens a NEW tab
// with the token stashed in ``sessionStorage`` (tab-scoped) so the god
// admin's own admin session in ``localStorage`` remains untouched.
export const godImpersonateUser = (userId) => api.post(`/api/admin/god/impersonate/${userId}`).then((r) => r.data);

// The upline (sponsor), the email, balance changes, a new password, the admin role and
// the status need the member's username typed (any admin may change the username, email and
// sponsor; the server checks the typed username). The Users modal does not ask, so it is asked here.
const needsTypedUsername = (body) =>
  Boolean(body.email) ||
  typeof body.upline_user_id === 'string' ||
  Boolean(body.purchase_balance_delta) ||
  Boolean(body.withdrawal_balance_delta) ||
  Boolean(body.new_password) ||
  typeof body.is_admin === 'boolean' ||
  typeof body.status === 'string';

// A balance change carries an Idempotency-Key. The SAME key is reused when the
// same change is retried soon after a timeout or a 5xx, so it can never apply
// twice; it is dropped once the server has answered (2xx or 4xx), and after two
// minutes, so a deliberate repeat later is a new change.
const ADJUST_RETRY_WINDOW_MS = 2 * 60 * 1000;
const pendingAdjustKeys = new Map();
const adjustKeyFor = (userId, body) => {
  if (!body.purchase_balance_delta && !body.withdrawal_balance_delta) return null;
  const id = JSON.stringify([userId, body.purchase_balance_delta || '', body.withdrawal_balance_delta || '']);
  const held = pendingAdjustKeys.get(id);
  if (!held || Date.now() - held.at > ADJUST_RETRY_WINDOW_MS) {
    pendingAdjustKeys.set(id, { key: idempotencyHeaders()['Idempotency-Key'], at: Date.now() });
  }
  return { id, key: pendingAdjustKeys.get(id).key };
};

export const patchAdminUser = (userId, body) => {
  if (!needsTypedUsername(body)) {
    return api.patch(`/api/admin/users/${userId}`, body).then((r) => r.data);
  }
  const typed = window.prompt(
    "This changes the upline, email, money, password, admin rights or status of the member.\nType the member's exact username to confirm:",
  );
  if (typed === null || typed.trim() === '') {
    return Promise.reject({ response: { data: { detail: 'Cancelled — nothing was changed.' } } });
  }
  const adjust = adjustKeyFor(userId, body);
  const settle = () => adjust && pendingAdjustKeys.delete(adjust.id);
  return api
    .patch(`/api/admin/users/${userId}`, { ...body, confirm_username: typed.trim() }, adjust ? { headers: { 'Idempotency-Key': adjust.key } } : undefined)
    .then(
      (r) => {
        settle();
        return r.data;
      },
      (err) => {
        const status = err?.response?.status;
        if (status && status < 500) settle();
        throw err;
      },
    );
};

// God only: the member's username is typed to confirm (the Users modal only
// asks for a second click, so it is asked here).
export const purgeUserPositions = (userId) => {
  const typed = window.prompt("Purge ALL of this member's queued positions?\nType the member's exact username to confirm:");
  if (typed === null || typed.trim() === '') {
    return Promise.reject({ response: { data: { detail: 'Cancelled — nothing was changed.' } } });
  }
  return api.post(`/api/admin/users/${userId}/purge-positions`, { confirm: true, confirm_username: typed.trim() }).then((r) => r.data);
};

// Admin Deposits oversight subpage — paginated list with optional
// status filter ("pending"|"completed"|"voided") + username/payment_id
// search. See backend /api/admin/deposits for the response shape.
export const adminListDeposits = ({ page = 1, pageSize = 25, status = '', q = '', dates = {} } = {}) =>
  api
    .get('/api/admin/deposits', {
      params: { page, page_size: pageSize, status, q, ...dates },
    })
    .then((r) => r.data);

// Re-runs the IPN credit path for a single deposit. Idempotent — returns
// status: credited | already_credited | not_finished_yet | unknown_payment_id.
export const adminReplayDeposit = (paymentId) => api.post(`/api/admin/deposits/${encodeURIComponent(paymentId)}/replay`).then((r) => r.data);

// Re-runs the v2 crypto-first IPN credit path for a single deposit.
// Returns: { replay_result: { credited, reason, amount_cents }, fresh_np_payload: {...} }
// Caller is expected to normalize this to the v1 shape via _normalizeV2Replay.
export const adminReplayCryptoDeposit = (paymentId) => api.post(`/api/admin/payments/crypto/replay/${encodeURIComponent(paymentId)}`).then((r) => r.data);

export const reconcilePositionPool = () => api.post('/api/admin/positions/reconcile').then((r) => r.data);

export const adminSeedRoot = () => api.post('/api/admin/positions/seed-root').then((r) => r.data);

export const adminDeleteUser = (userId, confirmUsername) =>
  api.delete(`/api/admin/users/${userId}`, { params: { confirm_username: confirmUsername } }).then((r) => r.data);

export const adminFactoryReset = (confirmPhrase) => api.post('/api/admin/system/factory-reset', { confirm_phrase: confirmPhrase }).then((r) => r.data);

// ---------- Subpage 2: Profit ledger matrix --------------------------------
export const fetchAdminProfit = (range = 'all') => api.get('/api/admin/profit', { params: { range } }).then((r) => r.data);

export const fetchAdminPlatformStats = () => api.get('/api/admin/platform-stats').then((r) => r.data);

// ----- Banner Ad Manager (admin) -------------------------------------------
export const fetchAdminBannerSummary = () => api.get('/api/admin/banner-ads/summary').then((r) => r.data);
export const listAdminBanners = (page = 1, status, dates = {}) => api.get('/api/admin/banner-ads', { params: { page, status, ...dates } }).then((r) => r.data);
export const approveBanner = (bannerId) => api.patch(`/api/admin/banner-ads/${bannerId}/approve`).then((r) => r.data);
export const rejectBanner = (bannerId, reason) => api.patch(`/api/admin/banner-ads/${bannerId}/reject`, { reason }).then((r) => r.data);
export const disableBanner = (bannerId) => api.patch(`/api/admin/banner-ads/${bannerId}/disable`).then((r) => r.data);

// One Idempotency-Key per click: a retried request (lost reply, failover) replays the first answer
// instead of queueing a second payout.
export const adminWithdraw = (body) => api.post('/api/admin/withdraw', body, { headers: idempotencyHeaders() }).then((r) => r.data);

// ---------- Subpage 3: Mass payout center ----------------------------------
export const listPendingWithdrawals = (page = 1, dates = {}) => api.get('/api/admin/withdrawals/pending', { params: { page, ...dates } }).then((r) => r.data);

// "Check All — All Pages". The API pages the pending ids 500 at a time (plan §7, R-2: no unbounded
// query); this collects every page and returns the legacy single-response shape the payout table
// expects: { total, ids, rows }. An API that answers without `total_pages` (the legacy one) is read
// as a single page. Ids are de-duplicated in case a row moves between pages mid-walk. `dates` must be
// the pending list's own date filter, so Check All selects exactly what the list shows.
const PENDING_IDS_PAGE_SIZE = 500;
const PENDING_IDS_MAX_PAGES = 400;

export const listPendingWithdrawalIds = async (dates = {}) => {
  const seen = new Set();
  const rows = [];
  for (let page = 1; page <= PENDING_IDS_MAX_PAGES; page += 1) {
    const { data } = await api.get('/api/admin/withdrawals/pending/ids', {
      params: { page, page_size: PENDING_IDS_PAGE_SIZE, ...dates },
    });
    const pageRows = Array.isArray(data?.rows) ? data.rows : [];
    for (const row of pageRows) {
      if (seen.has(row.withdrawal_id)) continue;
      seen.add(row.withdrawal_id);
      rows.push(row);
    }
    const totalPages = Number(data?.total_pages) || 1;
    if (page >= totalPages || pageRows.length === 0) break;
  }
  // ids come from the rows so the two can never disagree (the table selects from `rows`).
  const ids = rows.map((row) => row.withdrawal_id);
  return { total: ids.length, ids, rows };
};

// Settled (status=completed) withdrawals — newest first, 50/page.
// Powers the "Completed Payouts" section on the mass payout page so
// the operator can audit historical settlements without leaving the
// admin UI.
// `dates` filter the completed date here (the requested date on Pending and Processing).
export const listCompletedWithdrawals = (page = 1, dates = {}) =>
  api.get('/api/admin/withdrawals/completed', { params: { page, ...dates } }).then((r) => r.data);

// Patch v14: Processing withdrawals — dispatched to NOWPayments but not
// yet flipped to completed by the payout IPN. Powers the new "Processing
// Payouts" section between Pending and Completed.
export const listProcessingWithdrawals = (page = 1, dates = {}) =>
  api.get('/api/admin/withdrawals/processing', { params: { page, ...dates } }).then((r) => r.data);

// Patch v14: Cancel a still-pending withdrawal and atomically refund the
// user's withdrawal_balance. 409 if the row has already moved to processing
// or any non-pending status.
export const rejectWithdrawal = (withdrawalId, reason) =>
  api
    .post(`/api/admin/withdrawals/${encodeURIComponent(withdrawalId)}/reject`, {
      reason: reason || null,
    })
    .then((r) => r.data);

// Patch v14: Manually finalize a withdrawal stuck in processing — used
// when the NOWPayments payout IPN never reaches the server but admins
// have verified on-chain that the user was actually paid. Optional
// tx_hash + note for audit trail.
export const markWithdrawalCompleted = (withdrawalId, { tx_hash, note } = {}) =>
  api.post(`/api/admin/withdrawals/${encodeURIComponent(withdrawalId)}/mark-completed`, { tx_hash: tx_hash || null, note: note || null }).then((r) => r.data);

// One Idempotency-Key per click: a retried request replays the first answer and can never submit a
// second NOWPayments batch.
export const executeBatch = (withdrawalIds, notes) =>
  api.post('/api/admin/withdrawals/execute-batch', { withdrawal_ids: withdrawalIds, notes }, { headers: idempotencyHeaders() }).then((r) => r.data);

// Live USD-per-asset rate quotes (LTC/TRX/DOGE). Powers the Live
// Multi-Cryptocurrency Calculation Matrix display so the on-screen
// rate matches the rate the backend will use to compile the actual
// payout. Backend caches 60s; safe to poll on the same cadence.
// Returns shape: { rates: { LTC: { usd, source, age_seconds }, ... } }
export const fetchLivePayoutRates = () => api.get('/api/admin/rates/live').then((r) => r.data);

// ---------- Subpage 4: Support tickets -------------------------------------
// inbox: all | new | continued | waiting | resolved; search matches subject, username or email.
// `dates` filter the latest activity (the inbox's sort).
export const listAdminTickets = (page = 1, inbox = 'all', search = '', dates = {}) =>
  api.get('/api/admin/tickets', { params: { page, inbox, search: search || undefined, ...dates } }).then((r) => r.data);

export const replyAdminTicket = (ticketId, adminReply, resolve = true) =>
  api.post(`/api/admin/tickets/${ticketId}/reply`, { admin_reply: adminReply, resolve }).then((r) => r.data);

// status: Open | Pending | Resolved (resolve or reopen without replying).
export const setAdminTicketStatus = (ticketId, status) => api.post(`/api/admin/tickets/${ticketId}/status`, { status }).then((r) => r.data);

// Deletes the tickets (up to 200 ids) → { deleted, not_found }.
export const deleteAdminTickets = (ticketIds) => api.post('/api/admin/tickets/delete', { ticket_ids: ticketIds }).then((r) => r.data);

// ---------- Subpage 5: Financial audit -------------------------------------
export const listAdminAudit = (params = {}) => api.get('/api/admin/audit', { params }).then((r) => r.data);

// ---------- Subpage 6: System variable governor ----------------------------
export const fetchAdminConfig = () => api.get('/api/admin/config').then((r) => r.data);

// A refusal may carry {code, message} as its detail; the config screens toast
// the detail as text, so it is flattened to the message first.
const flattenDetail = (err) => {
  const data = err?.response?.data;
  if (data?.detail && typeof data.detail === 'object') {
    data.detail = data.detail.message || data.detail.code || 'Request failed';
  }
  throw err;
};

export const updateAdminConfig = (body) => api.put('/api/admin/config', body).then((r) => r.data, flattenDetail);

// ---------- Launch Control (Pre-Launch Mode + countdown + refund) --------
// Read the current Launch Control state. Reuses /api/admin/config so we
// don't introduce a new endpoint just for two fields. The response also
// includes ``heartbeat_pacing_ratio`` which the Launch Control UI shows
// as the engine pause toggle (0 = paused).
export const fetchLaunchControl = () => api.get('/api/admin/config').then((r) => r.data);

// Update any combination of pre_launch_mode_enabled, live_payments_enabled,
// root_sidelining_enabled, launch_at_iso, and heartbeat_pacing_ratio in a
// single PUT. Other config keys are untouched.
export const updateLaunchControl = ({ preLaunchEnabled, livePaymentsEnabled, rootSideliningEnabled, launchAtIso, heartbeatPacingRatio }) => {
  const body = {};
  if (typeof preLaunchEnabled === 'boolean') {
    body.pre_launch_mode_enabled = preLaunchEnabled;
  }
  if (typeof livePaymentsEnabled === 'boolean') {
    body.live_payments_enabled = livePaymentsEnabled;
  }
  if (typeof rootSideliningEnabled === 'boolean') {
    body.root_sidelining_enabled = rootSideliningEnabled;
  }
  if (launchAtIso !== undefined) {
    body.launch_at_iso = launchAtIso;
  }
  if (heartbeatPacingRatio !== undefined && heartbeatPacingRatio !== null) {
    body.heartbeat_pacing_ratio = String(heartbeatPacingRatio);
  }
  return api.put('/api/admin/config', body).then((r) => r.data, flattenDetail);
};

// Destructive: refund every Sub Pack + Single Position purchase to its
// buyer's purchase_balance, mark Sub Packs inactive, drain pool queues.
// Backend enforces pre_launch_mode_enabled === true before accepting.
export const refundAllPurchases = (confirmPhrase) =>
  api
    .post('/api/admin/system/refund-all-purchases', {
      confirm_phrase: confirmPhrase,
    })
    .then((r) => r.data);

// ---------- Subpack Settings (dedicated subpage) --------------------------
// Per-tier release-interval override controls. Reads return the active
// interval (override-or-spec), the spec default, the source flag, and
// the active-pack count so the operator can audit what "apply-to-active"
// would mutate.
export const fetchSubpackSettings = () => api.get('/api/admin/subpacks/settings').then((r) => r.data);

// Body shape: { intervals: { "5": 32.0, "10": 16.0, ... } }
// Empty/missing tier keys in the map are treated as "reset to spec default".
export const updateSubpackIntervals = (intervals) => api.put('/api/admin/subpacks/settings', { intervals }).then((r) => r.data);

// Patch v18.3 — Full Global Economics + Tier Ladder update path.
// Body shape:
//   {
//     position_price_usd: "1.00",
//     payout_cycler:      "2.50",
//     payout_sponsor:     "0.25",
//     payout_admin_fee:   "0.25",
//     tier_ladder: [
//       { tier_price_usd: 5,  total_positions: 5,  interval_seconds: 115200 },
//       ...
//     ]
//   }
// Server enforces sum invariant (cycler + sponsor + admin_fee == price * 3)
// on the merged result of the supplied fields + current live values.
// Every field is optional; only supplied fields are updated.
// Returns the same envelope as fetchSubpackSettings() (post-update state).
export const updateSubpackEconomics = (payload) => api.put('/api/admin/subpacks/settings', payload).then((r) => r.data);

// Body shape: { tier_price_usd?: number, confirm_phrase: "APPLY" }
// Omit tier_price_usd to apply to every supported tier in one call.
export const applySubpackIntervalsToActive = ({ tierPriceUsd, confirmPhrase }) =>
  api
    .post('/api/admin/subpacks/apply-to-active', {
      tier_price_usd: tierPriceUsd ?? null,
      confirm_phrase: confirmPhrase,
    })
    .then((r) => r.data);

// ---------- Active Subpacks Oversight (read-only) -------------------------
// Paginated list of every Sub Pack currently dripping on the platform.
// Backend serves 50/page (fixed), newest first, with usernames pre-joined.
// Response shape:
//   { active_count, completed_count, items: [...], page, page_size, total, total_pages }
// `dates` filter the purchase date; `total` is the filtered count the pager pages.
// IMPORTANT: this powers the Active Subpacks admin subpage which auto-
// refreshes at 60s intervals to stay inside the v6/v7 polling guardrails
// (do NOT poll this endpoint faster than 60s anywhere).
export const fetchActiveSubpacks = (page = 1, dates = {}) => api.get('/api/admin/subpacks/active', { params: { page, ...dates } }).then((r) => r.data);

// ---------- Proof of Work: Random Selection (read-only) -------------------
// Returns the last 50 selected fillers + last 50 selected roots from the
// heartbeat's capped ``cycle_selections`` log. Powers the v10 "Proof of
// Work" admin subpage which lets operators visually confirm the v9
// random engine is genuinely picking positions at random (jumbled
// Purchase Timestamps = random; strictly ascending = FIFO regression).
// Response shape:
//   { fillers: [...], roots: [...], log_size, log_cap,
//     fillers_returned, roots_returned }
// IMPORTANT: same v6/v7 polling guardrail \u2014 page auto-refreshes at 60s.
export const fetchProofOfWorkSelections = () => api.get('/api/admin/proof-of-work/selections').then((r) => r.data);

// Live engine HUD for the admin Proof of Work page (owner request, 28 Sep 2026). Admin area only.
export const fetchProofOfWorkLive = () => api.get('/api/admin/proof-of-work/live').then((r) => r.data);

// ---------- Dashboard: Operator P&L stats ----------------------------------
export const fetchAdminExternalPnL = () => api.get('/api/admin/pnl/external').then((r) => r.data);

export const fetchAdminInternalPnL = () => api.get('/api/admin/pnl/internal').then((r) => r.data);

// ---------- Payments: NOWPayments live diagnostics + 2FA verify (Step 6B) -
export const fetchPaymentsPreflight = () => api.get('/api/admin/payments/preflight').then((r) => r.data);

// Newest-first payout batches, server-paged: { items: [{ batch_id, status, created_at, selected_count, total_usd, nowpayments_batch_id, ... }],
// count, page, page_size, total, total_pages }. `dates` filter the compiled date; `statuses` (array) limits the
// batch statuses (e.g. ['awaiting_2fa', 'verifying'] for the Awaiting 2FA panel); empty = every status.
export const listRecentPayoutBatches = (page = 1, limit = 25, dates = {}, statuses = []) =>
  api
    .get('/api/admin/withdrawals/batches/recent', { params: { page, limit, ...dates, ...(statuses.length ? { status: statuses.join(',') } : {}) } })
    .then((r) => r.data);

export const verifyPayoutBatchTwoFA = (batchId, verificationCode) =>
  api.post(`/api/admin/withdrawals/batches/${encodeURIComponent(batchId)}/verify-2fa`, { verification_code: String(verificationCode) }).then((r) => r.data);

// ---------- Patch v15: Platform Unwind ------------------------------------
// God-admin-only. Drives the new "Platform Unwind" subpanel on Launch
// Control. Two operations, each with a preview (dry-run) endpoint and an
// execute endpoint. Execute requires the operator to type the exact dollar
// amount returned by the preview as the confirm_phrase; the server re-runs
// the math and rejects if the total has drifted since preview.

// 1) Refund Net Crypto Deposits ---------------------------------------------
// Preview: GET — returns
//   { users_affected, total_refund_usd, total_refund_usd_cents,
//     confirm_phrase, top_users: [...], generated_at }
export const fetchPlatformUnwindRefundPreview = () => api.get('/api/admin/platform-unwind/refund-deposits/preview').then((r) => r.data);

// Execute: POST — body { confirm_phrase: "1234.56" }
// Returns { status, batch_id, users_affected, total_credited_usd,
//           total_debited_usd, errors, started_at, finished_at }
// status === "confirm_mismatch" means the total drifted since preview.
export const executePlatformUnwindRefund = (confirmPhrase) =>
  api
    .post('/api/admin/platform-unwind/refund-deposits/execute', {
      confirm_phrase: String(confirmPhrase),
    })
    .then((r) => r.data);

// 2) Clear All Withdrawal Balances ------------------------------------------
// Preview: GET — returns
//   { users_affected, total_clear_usd, total_clear_usd_cents,
//     confirm_phrase, top_users: [...], generated_at }
export const fetchPlatformUnwindClearPreview = () => api.get('/api/admin/platform-unwind/clear-withdrawals/preview').then((r) => r.data);

// Execute: POST — body { confirm_phrase: "1234.56" }
// Returns { status, batch_id, users_affected, total_cleared_usd, errors,
//           started_at, finished_at }
export const executePlatformUnwindClear = (confirmPhrase) =>
  api
    .post('/api/admin/platform-unwind/clear-withdrawals/execute', {
      confirm_phrase: String(confirmPhrase),
    })
    .then((r) => r.data);

// 3) Reset Matrix State (Patch v15.1) -------------------------------------
// Drains position queues, wipes rotator_pool, deactivates active Sub Packs,
// resets per-user cycle counters, and zeroes the global Redis cycle metric.
// Touches NO balances. The confirm_phrase is the live queue entry count
// (as a plain integer string, e.g. "196841"), NOT a dollar amount.
//
// Preview: GET — returns
//   { queue_entries_total, queue_entries_root, queue_entries_filler,
//     active_subpacks_count, archived_cycles_count,
//     users_with_cycle_counters, redis_global_cycle_counter,
//     confirm_phrase, display_total, display_subtitle,
//     users_affected, top_users: [...], generated_at }
export const fetchPlatformUnwindResetMatrixPreview = () => api.get('/api/admin/platform-unwind/reset-matrix/preview').then((r) => r.data);

// Execute: POST — body { confirm_phrase: "<queue_entries_total_str>" }
// Returns { status, batch_id, action, preview_snapshot,
//           subpacks_deactivated_count, user_counters_reset_count,
//           inner_reset_response, errors, started_at, finished_at }
// status === "confirm_mismatch" means the queue count drifted since preview.
export const executePlatformUnwindResetMatrix = (confirmPhrase) =>
  api
    .post('/api/admin/platform-unwind/reset-matrix/execute', {
      confirm_phrase: String(confirmPhrase),
    })
    .then((r) => r.data);

// ---------- Patch v18.0 — Cheaters (Fraud & Reconciliation) API ----------
// God-Admin only endpoints. Delegated admins receive HTTP 403.
// Flagged members, newest flag first, 50/page: { items, page, page_size, total, total_pages }. `dates` filter the flag date.
export const listAdminCheaters = (page = 1, dates = {}) => api.get('/api/admin/cheaters', { params: { page, ...dates } }).then((r) => r.data);

export const flagAdminUser = (userId, reason) => api.post(`/api/admin/cheaters/${userId}/flag`, { reason: reason || 'manual_admin_flag' }).then((r) => r.data);

export const unflagAdminUser = (userId, reason) =>
  api.post(`/api/admin/cheaters/${userId}/unflag`, { reason: reason || 'admin_reviewed_false_positive' }).then((r) => r.data);

// Hard-delete requires the God Admin to retype the exact username of the
// target account. The backend refuses if `confirm_username` mismatches.
export const deleteAdminCheater = (userId, confirmUsername) =>
  api.post(`/api/admin/cheaters/${userId}/delete`, { confirm_username: confirmUsername }).then((r) => r.data);

// Newest first, `limit` per page: { items, drift_summary, page, page_size, total, total_pages }. `dates` filter the row time.
export const fetchAdminCheaterLedger = (userId, { page = 1, limit = 200, dates = {} } = {}) =>
  api.get(`/api/admin/cheaters/${userId}/ledger`, { params: { page, limit, ...dates } }).then((r) => r.data);

// One-shot on-demand reconciliation scan (in addition to the 5-min cron).
export const runAdminReconcileNow = () => api.post('/api/admin/cheaters/reconcile-now').then((r) => r.data);

// ---------- Threat telemetry: Active Ban Directory -------------------------
// One row per banned IP, newest ban first, server-paged: { items, page, page_size, total, total_pages }.
// `dates` filter the latest ban time.
export const fetchAdminActiveBans = (page = 1, dates = {}) => api.get('/api/admin/threat/bans', { params: { page, ...dates } }).then((r) => r.data);
